import { useEffect } from 'react'

/**
 * Sets every serif text block's max-width to just over half its single-line length,
 * so (with text-wrap: balance from .font-serif) it always breaks into two even lines.
 * Re-measures on resize and once web fonts have loaded. If half the text is wider
 * than the available space (e.g. on phones), it simply wraps naturally.
 */
export function useTwoLineSerif() {
  useEffect(() => {
    let raf = 0

    const apply = () => {
      raf = 0
      const els = document.querySelectorAll<HTMLElement>('.font-serif')
      els.forEach((el) => {
        // Blocks that set their own line breaks are left alone
        if (el.dataset.lines === 'manual') return
        // Measure the text on a single line
        el.style.maxWidth = 'none'
        el.style.whiteSpace = 'nowrap'
        const range = document.createRange()
        range.selectNodeContents(el)
        const lineWidth = range.getBoundingClientRect().width
        el.style.whiteSpace = ''
        if (!lineWidth) {
          el.style.maxWidth = ''
          return
        }
        // Just over half, so two balanced lines fit and a third never appears
        el.style.maxWidth = `${Math.ceil(lineWidth / 2 + lineWidth * 0.04)}px`
      })
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }

    schedule()
    document.fonts?.ready.then(schedule)
    window.addEventListener('resize', schedule)
    return () => {
      window.removeEventListener('resize', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
}
